import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { KMark } from "@/components/KMark";

export const Route = createFileRoute("/auth")({
  head: () => ({
    meta: [
      { title: "Sign in — thatKorner" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AuthPage,
});

function AuthPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (mode === "signin") {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
      } else {
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: { emailRedirectTo: `${window.location.origin}/admin` },
        });
        if (error) throw error;
      }
      navigate({ to: "/admin" });
    } catch (err: any) {
      setError(err?.message || "Authentication failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="flex min-h-[calc(100vh-60px)] items-center justify-center bg-ink px-5 py-24">
      <div className="w-full max-w-md rounded-3xl border border-bone/10 bg-bone/[0.02] p-10">
        <div className="flex items-center justify-center">
          <KMark tone="ember" className="h-12 w-12" />
        </div>
        <h1 className="h-display mt-6 text-center text-3xl text-bone">
          {mode === "signin" ? "Admin sign-in" : "Create admin account"}
        </h1>
        <form onSubmit={onSubmit} className="mt-8 space-y-4">
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-bone/60">Email</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-2 w-full rounded-lg border border-bone/15 bg-ink px-3 py-2.5 text-sm text-bone outline-none focus:border-ember"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-bone/60">Password</label>
            <input
              type="password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-2 w-full rounded-lg border border-bone/15 bg-ink px-3 py-2.5 text-sm text-bone outline-none focus:border-ember"
            />
          </div>
          {error && <p className="text-xs text-red-400">{error}</p>}
          <button
            type="submit"
            disabled={busy}
            className="inline-flex w-full items-center justify-center rounded-full bg-ember px-7 py-3.5 font-display text-sm font-bold uppercase tracking-[0.2em] text-bone transition-all hover:bg-ember/90 disabled:opacity-60"
          >
            {busy ? "Please wait…" : mode === "signin" ? "Sign in" : "Create account"}
          </button>
        </form>
        <button
          onClick={() => setMode(mode === "signin" ? "signup" : "signin")}
          className="mt-6 block w-full text-center text-xs text-bone/60 hover:text-bone"
        >
          {mode === "signin" ? "Need an account? Sign up" : "Have an account? Sign in"}
        </button>
      </div>
    </section>
  );
}
